import { useState, useEffect } from 'react';
import { supabase } from '../../supabase/supabaseClient';

/**
 * Hook para cargar assets desde Supabase Storage
 * 
 * @param {string} path - Ruta del archivo en el bucket
 * @param {string} bucket - Nombre del bucket (default: 'site-assets')
 * @param {object} transform - Opciones de optimización (solo para imágenes, no videos)
 *   @param {number} transform.width - Ancho máximo en px
 *   @param {number} transform.quality - Calidad 1-100 (default: 75)
 *   @param {string} transform.format - Formato: 'webp' | 'avif' | 'origin'
 * 
 * @example - Sin optimización (videos, logos)
 * const { url } = useSupabaseAsset('videos/hero-video.mp4');
 * 
 * @example - Con optimización (imágenes de contenido)
 * const { url } = useSupabaseAsset('events/foto.jpg', 'site-assets', { width: 800, quality: 75, format: 'webp' });
 */
export const useSupabaseAsset = (path, bucket = 'site-assets', transform = null) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }

    const loadAsset = async () => {
      try {
        setLoading(true);
        setError(null);

        // ✅ Si hay opciones de transformación, usar la API de render de Supabase
        if (transform) {
          // Construir URL de render manualmente (más fiable que getPublicUrl con transform)
          const baseUrl = `${process.env.VITE_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL}`;
          const renderUrl = `${baseUrl}/storage/v1/render/image/public/${bucket}/${path}?width=${transform.width || 800}&quality=${transform.quality || 75}`;
          setUrl(renderUrl);
        } else {          
          // Sin transformación (igual que antes - no rompe nada)
          const { data } = supabase.storage
            .from(bucket)
            .getPublicUrl(path);

          if (data?.publicUrl) {
            setUrl(data.publicUrl);
          } else {
            setError('No se pudo obtener la URL');
          }
        }
      } catch (err) {
        console.error('Error loading asset:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAsset();
  }, [path, bucket, JSON.stringify(transform)]);

  return { url, loading, error };
};

/**
 * Hook para cargar múltiples assets desde Supabase Storage
 */
export const useSupabaseAssets = (paths, bucket = 'site-assets') => {
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!paths || paths.length === 0) {
      setLoading(false);
      return;
    }

    const loadAssets = async () => {
      try {
        setLoading(true);
        const loadedUrls = {};
        const loadedErrors = {};

        for (const path of paths) {
          try {
            const { data } = supabase.storage
              .from(bucket)
              .getPublicUrl(path);

            if (data?.publicUrl) {
              loadedUrls[path] = data.publicUrl;
            }
          } catch (err) {
            loadedErrors[path] = err.message;
          }
        }

        setUrls(loadedUrls);
        setErrors(loadedErrors);
      } catch (err) {
        console.error('Error loading assets:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAssets();
  }, [JSON.stringify(paths), bucket]);

  return { urls, loading, errors };
};

/**
 * Hook para subir archivos a Supabase Storage
 */
export const useSupabaseUpload = (bucket = 'site-assets') => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);

  const upload = async (file, path) => {
    setUploading(true);
    setUploadError(null);
    setUploadedUrl(null);

    try {
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);

      if (data?.publicUrl) {
        setUploadedUrl(data.publicUrl);
        return data.publicUrl;
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setUploadError(err.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  return { upload, uploading, uploadError, uploadedUrl };
};

export default useSupabaseAsset;